import React, { forwardRef, useId, useState } from 'react';
import { Box } from '@mui/material';

import { TextInput } from './TextInput';
import type { TextInputProps } from './TextInput';
import { formatNumber, isNumberText, parseNumberText } from './numberParsing';

export interface NumberInputProps extends Omit<TextInputProps, 'type' | 'value' | 'defaultValue' | 'onValueChange' | 'onChange' | 'multiline' | 'clearable' | 'onClear' | 'startAdornment' | 'endAdornment'> {
  value: number | null;
  onValueChange: (value: number | null) => void;
  unit?: React.ReactNode;
  unitPosition?: 'start' | 'end';
  allowNegative?: boolean;
  allowDecimal?: boolean;
}

const joinIds = (...ids: ReadonlyArray<string | undefined>): string | undefined => {
  const present = ids.filter((id): id is string => Boolean(id));
  return present.length === 0 ? undefined : present.join(' ');
};

export const NumberInput = forwardRef<HTMLDivElement, NumberInputProps>(function NumberInput(
  { value, onValueChange, unit, unitPosition = 'end', allowNegative = true, allowDecimal = true, inputProps, describedById, ...rest },
  ref,
) {
  const [text, setText] = useState(() => formatNumber(value));
  const unitId = useId();
  const display = parseNumberText(text) === value ? text : formatNumber(value);
  const unitTag = unit ? (
    <Box component="span" id={unitId}>
      {unit}
    </Box>
  ) : undefined;

  return (
    <TextInput
      ref={ref}
      type="text"
      value={display}
      inputProps={{ inputMode: 'decimal', ...inputProps }}
      describedById={joinIds(unitTag ? unitId : undefined, describedById)}
      startAdornment={unitPosition === 'start' ? unitTag : undefined}
      endAdornment={unitPosition === 'end' ? unitTag : undefined}
      onChange={(event) => {
        const next = event.target.value;
        if (!isNumberText(next, { allowNegative, allowDecimal })) return;
        setText(next);
        const parsed = parseNumberText(next);
        if (parsed !== value) onValueChange(parsed);
      }}
      {...rest}
    />
  );
});
